import TableBody from '@mui/material/TableBody';
import TableRow from '@mui/material/TableRow';
import { FC, ReactNode, useEffect, useState } from 'react';
import { BsPeople } from 'react-icons/bs';
import { FiEdit, FiSearch } from 'react-icons/fi';
import { HiPlus } from 'react-icons/hi';
import { TbTrash } from 'react-icons/tb';
import { useLocation, useNavigate } from 'react-router-dom';
import { AdminTitle } from '../../components/AdminTitle';
import { Loading } from '../../components/Loading';
import { StatusModal } from '../../components/StatusModal';
import { StyledButton } from '../../components/StyledButton';
import { StyledInput } from '../../components/StyledInput';
import { StatusModalConfirmClick } from '../../components/StyledModalConfirmClick';
import NewDataTable from '../../components/StyledTableContentRaw';
import { StyledTooltip } from '../../components/StyledTooltip';
import { useUser } from '../../context/UserContext';
import { deleteUser, useUsersList } from '../../hooks/useUsers';
import { Actions, FiltersContainer, UsersContainer } from './style';
import { useQueryClient } from 'react-query';
import { formatDocument } from '../../utils/formatDocument';
import { TYPE_DOCUMENT } from '../../utils/enums';
import formatMobilePhoneNumber from '../../utils/formatMobilePhoneNumber';

const Users: FC = () => {
  const { user } = useUser();
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const [loading, setLoading] = useState(false);

  const [textToShow, setTextToShow] = useState('');
  const [text, setText] = useState<string | undefined>(undefined);

  const [order, setOrder] = useState<'desc' | 'asc'>('desc');
  const [orderBy, setOrderBy] = useState('created_at');
  const [page, setPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);

  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [typeConfirmModal, setTypeConfirmModal] = useState<
    'success' | 'error' | 'warning' | 'info' | 'question'
  >('question');
  const [messageConfirmModal, setMessageConfirmModal] = useState<
    string | ReactNode
  >('');
  const [arrayActionsConfirmModal, setArrayActionsConfirmModal] = useState<
    Array<() => void>
  >([]);
  const [showStatusModal, setShowStatusModal] = useState(false);
  const [typeStatusModal, setTypeStatusModal] = useState<'success' | 'error'>(
    'success',
  );
  const [messageStatusModal, setMessageStatusModal] = useState<
    string | ReactNode
  >('');
  const [messageTitleStatusModal] = useState('');

  const { data, isLoading, error, isFetched } = useUsersList({
    resultsPerPage: itemsPerPage,
    page: page,
    text: text && text?.length > 2 ? text.trim() : undefined,
    orderByDirection: order,
    orderBy,
  });

  const headers = [
    { key: 'name', label: 'Nome' },
    { key: 'email', label: 'E-mail' },
    { key: 'document', label: 'CPF/CNPJ' },
    { key: 'phone', label: 'Telefone' },
    { key: 'status', label: 'Ativo' },
    { key: 'role', label: 'Função' },
    { key: '', label: 'Ações' },
  ];

  const handleFilterPlateOrUserValue = (event: any) => {
    const upperCaseValue = event.target.value;
    setTextToShow(upperCaseValue);

    upperCaseValue && setText(upperCaseValue.toUpperCase());
  };

  const handleSetPlateOrUserValue = () => {
    text && setText(text.toUpperCase());
  };

  const handleModalToDeleteUser = async (info: any) => {
    setTypeConfirmModal('question');
    setMessageConfirmModal(`Deseja realmente excluir este usuário?`);
    setArrayActionsConfirmModal([() => handleDeleteUser(info.id)]);
    setShowConfirmModal(true);
  };

  const handleDeleteUser = async (id: number) => {
    setLoading(true);

    try {
      await deleteUser(id);
      setTypeStatusModal('success');
      setMessageStatusModal('Registro deletado com sucesso');
    } catch (err) {
      setTypeStatusModal('error');
      setMessageStatusModal('Não foi possível deletar registros');
    } finally {
      setLoading(false);
      setShowStatusModal(true);
      queryClient.invalidateQueries('user_frotas');
    }
  };

  return (
    <UsersContainer>
      {loading && <Loading />}
      {isLoading && <Loading />}
      {showConfirmModal && (
        <StatusModalConfirmClick
          type={typeConfirmModal}
          text={messageConfirmModal}
          setModal={setShowConfirmModal}
          callActions={arrayActionsConfirmModal}
          twoButtons={true}
        />
      )}
      {showStatusModal && (
        <StatusModal
          type={typeStatusModal}
          title={messageTitleStatusModal}
          text={messageStatusModal}
          setModal={setShowStatusModal}
        />
      )}
      <AdminTitle
        title="Listagem de Usuários"
        icon={<BsPeople size={24} color="#4D4C4D" />}
      />

      <FiltersContainer>
        <label htmlFor="">
          <StyledInput
            placeholder="Buscar por nome ou documento"
            value={textToShow}
            onChange={handleFilterPlateOrUserValue}
          />
          <span onClick={handleSetPlateOrUserValue}>
            <FiSearch />
          </span>
        </label>

        <StyledButton
          text="Novo cadastro"
          icon={<HiPlus size={22} />}
          iconPosition="left"
          backgroundColor="#005EAD"
          onClick={() => navigate('create')}
        />
      </FiltersContainer>

      <NewDataTable
        title="Listagem de Usuários"
        heads={headers}
        hasPagination
        data={data?.results}
        order={order}
        setOrder={setOrder}
        orderBy={orderBy}
        setOrderBy={setOrderBy}
        page={page}
        setPage={setPage}
        setItemsPerPage={setItemsPerPage}
        nPages={data?.nPages}
        isLoading={isLoading}
        error={error}
        totalItems={10}
      >
        <TableBody>
          {data?.results?.map((each, i) => (
            <TableRow key={i}>
              <td>{each.name}</td>
              <td>{each.email}</td>
              <td>
                {formatDocument(
                  each.document ?? '',
                  each.document?.length === 11
                    ? TYPE_DOCUMENT.CPF
                    : TYPE_DOCUMENT.CNPJ,
                )}
              </td>
              <td>
                {each?.phone ? formatMobilePhoneNumber(each?.phone) : '--'}
              </td>
              <td>{each.status}</td>
              <td>{each.role}</td>
              <td style={{ textAlign: 'center' }}>
                <Actions>
                  <StyledTooltip text="Editar" position="top" arrow>
                    <div>
                      <FiEdit
                        size={22}
                        onClick={() => navigate(`edit/${each.id}`)}
                      />
                    </div>
                  </StyledTooltip>
                  <StyledTooltip text="Deletar" position="top" arrow>
                    <div>
                      <TbTrash
                        size={24}
                        onClick={() => handleModalToDeleteUser(each)}
                      />
                    </div>
                  </StyledTooltip>
                  <div></div>
                </Actions>
              </td>
            </TableRow>
          ))}
        </TableBody>
      </NewDataTable>
    </UsersContainer>
  );
};

export default Users;
